import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronRight, Download, FileText } from 'lucide-react';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import { EngagementNameList } from '../components/workflow/CoverageDisplay';
import { QcFormView } from '../components/workflow/QcForm';
import { LegacyImportBadge, SignedFormPendingNotice, FormPdfDownload } from '../components/records/legacyImport';
import WorkflowAddendaList from '../components/records/WorkflowAddendaList';
import { RecordTimeline } from './QMQcRecords';
import NowName, { nowLabel } from '../components/shared/NowName';
import CompanyList from '../components/workflow/CompanyList';
import { formatDate } from '../utils/dateFormat';
import { fetchJSON, API_BASE_URL } from '../utils/api';
import { eqrLabel } from './qcAddendum';

// A QC6 / QC7 record, read-only, from the workflow side
// (docs/modules/workflow-qcad.md §5). This is what an addendum's "QC Form
// Reference" opens: the record AS IT STANDS TODAY — a QC Amendment edits it
// in place, so the application that first produced it may no longer say what
// the form says — with every addendum filed on it and its history. The
// Management page (/qm/qc-records/:id) stays the edit surface behind its own
// key; this page needs only what the server's canViewRecord allows (a
// submitter, the reviewer roster, the covered engagements' people).
const WorkflowQcadRecord = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await fetchJSON(`/api/workflow/qcad/record/${id}`);
        if (!cancelled) setData(d);
      } catch (e) { if (!cancelled) setError(e.message); }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const record = data?.record || null;
  const risk = data?.risk || null;
  const base = `${API_BASE_URL}/api/workflow/qcad/record/${id}`;
  const current = (record?.files || []).filter((f) => !f.superseded);
  const previous = (record?.files || []).filter((f) => f.superseded);
  const entities = record?.entities || [];

  return (
    <RequestDetailShell
      backTo="/workflow/qcad"
      backLabel="QC6 / QC7 Addendum"
      loading={!record && !error}
      error={error}
    >
      {record && (
        <>
          <RequestHeader
            requestNo={`${record.qcType} · ${nowLabel(record.entityName, record.entityNameNow)}`}
            originLabel={record.approvedOn ? `approved on ${formatDate(record.approvedOn)}` : 'approval date not set'}
            badge={record.legacyImport ? <LegacyImportBadge /> : null}
          />
          <p className="text-sm text-slate-600 -mt-2 mb-4">
            The record as it stands today — an approved QC Amendment changes it in place. Addenda filed after its approval are listed below and are read together with the form.
          </p>

          <SignedFormPendingNotice pending={record.signedFormPending === true} />

          <SectionCard title="Record">
            <DetailGrid>
              <DetailItem label={entities.length > 1 ? `Companies Covered (${entities.length})` : 'Entity'}>
                {entities.length > 0 ? (
                  <CompanyList entities={entities} />
                ) : `${nowLabel(record.entityName, record.entityNameNow)}${record.uen ? ` (${record.uen})` : ''}`}
              </DetailItem>
              <DetailItem label="Client Group">{nowLabel(record.clientGroup, record.clientGroupNow) || '—'}</DetailItem>
              <DetailItem label="Financial Year End">{formatDate(record.yearEnd)}</DetailItem>
              <DetailItem label="Approval Date">{record.approvedOn ? formatDate(record.approvedOn) : '—'}</DetailItem>
              <DetailItem label={(record.engagements || []).length > 1 ? `Engagements Covered (${record.engagements.length})` : 'Engagement Covered'}>
                {(record.engagements || []).length === 0 ? '—'
                  : <EngagementNameList items={record.engagements} keyOf={(e, i) => e.engagementId || i} />}
              </DetailItem>
              <DetailItem label="Current Risk Rating">
                {risk?.rating || '—'}
                {risk?.revisedBy ? <span className="text-xs text-slate-500"> (revised by {risk.revisedBy}; originally {risk.original || '—'})</span> : null}
              </DetailItem>
              <DetailItem label="Current Engagement Quality Reviewer">
                <NowName value={eqrLabel(data?.eqr?.eqr)} now={data?.eqr?.eqrNow} />
                {data?.eqr?.revisedBy ? <span className="text-xs text-slate-500"> (set by {data.eqr.revisedBy}; the form named {eqrLabel(data.eqr.original)})</span> : null}
              </DetailItem>
              {record.remarks && <DetailItem label="Remarks">{record.remarks}</DetailItem>}
            </DetailGrid>
          </SectionCard>

          {(record.answers || record.submissionMethod === 'form') && (
            <SectionCard title={`${record.qcType} Form`}>
              <div className="flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  onClick={() => setShowForm((v) => !v)}
                  className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-800"
                >
                  <ChevronRight className={`w-4 h-4 transition-transform ${showForm ? 'rotate-90' : ''}`} />
                  {showForm ? 'Hide Answers' : 'Show Answers'}
                </button>
                {record.submissionMethod === 'form' && record.answers && (
                  <FormPdfDownload
                    record={record}
                    formName={record.qcType}
                    href={`${base}/pdf`}
                    signedHref={(f) => `${base}/files/${f.id}`}
                    className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800">
                    <Download className="w-4 h-4" /> Download {record.qcType} (PDF)
                  </FormPdfDownload>
                )}
              </div>
              {showForm && (
                <div className="mt-3">
                  <QcFormView qcType={record.qcType} answers={record.answers} engagements={record.engagements || []} />
                </div>
              )}
            </SectionCard>
          )}

          <SectionCard title={`Addenda (${(data.addenda || []).length})`}>
            <WorkflowAddendaList record={record} addenda={data.addenda || []} />
          </SectionCard>

          <SectionCard title="Files">
            {current.length === 0 && previous.length === 0 ? (
              <p className="text-sm text-slate-500">No files attached.</p>
            ) : (
              <>
                <ul className="divide-y divide-slate-100">
                  {current.map((f) => (
                    <li key={f.id} className="flex items-center gap-3 py-2">
                      <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-800 truncate">{f.originalName}</div>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
                          {f.docType === 'QC_FORM' ? 'Signed form (uploaded)' : 'Supporting'}
                        </span>
                      </div>
                      <a href={`${base}/files/${f.id}`} target="_blank" rel="noreferrer"
                        className="text-blue-600 hover:text-blue-800 shrink-0" title="Download">
                        <Download className="w-4 h-4" />
                      </a>
                    </li>
                  ))}
                </ul>
                {previous.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-dashed border-slate-200">
                    <h5 className="text-[11px] font-semibold text-slate-500 uppercase mb-1">Previous Versions</h5>
                    <ul className="divide-y divide-slate-100">
                      {previous.map((f) => (
                        <li key={f.id} className="flex items-center gap-3 py-1.5">
                          <FileText className="w-4 h-4 text-slate-300 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-slate-500 truncate">{f.originalName}</div>
                            <span className="text-[10px] text-slate-500">
                              {f.docType === 'QC_FORM' ? 'Signed form (uploaded)' : 'Supporting'}
                              {f.supersededByRef ? ` · replaced by ${f.supersededByRef}` : ' · replaced'}
                              {f.supersededAt ? ` on ${formatDate(f.supersededAt)}` : ''}
                            </span>
                          </div>
                          <a href={`${base}/files/${f.id}`} target="_blank" rel="noreferrer"
                            className="text-slate-400 hover:text-slate-600 shrink-0" title="Download">
                            <Download className="w-4 h-4" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </SectionCard>

          <RecordTimeline recordId={record.id} qcType={record.qcType} engagements={record.engagements || []} events={data.events || []} />
        </>
      )}
    </RequestDetailShell>
  );
};

export default WorkflowQcadRecord;
